import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  BackButton,
  FormField,
  FormGrid,
  FormSection,
  InlineFeedback,
  PrimaryButton,
  RutField,
} from 'components/forms';
import { COUNTRIES } from 'constants/countries';
import { CHILE_COMMUNES } from 'data/chileCommunes';
import moment from 'moment';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { Address, Client, EditableAdminClientAttributes } from 'services/types/client';
import type { User } from 'services/types/user';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import { normalizeNationalityToIso2 } from 'utils/normalizeNationality';
import canChangeAutomaticResponses from 'utils/validations/canChangeAutomaticResponses';
import validateEmail from 'utils/validations/validateEmail';

interface EditClientFormProps {
  client: Client;
  clientDraft: EditableAdminClientAttributes;
  setClientDraft: React.Dispatch<React.SetStateAction<EditableAdminClientAttributes | null>>;
  onSave: () => void;
  onCancel: () => void;
  currentUser?: User | null;
  clientDealership?: BusinessUnit;
  isSaving?: boolean;
  saveError?: string | null;
}

interface AdminToggleRowProps {
  label: string;
  helper?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

const AdminToggleRow: FC<AdminToggleRowProps> = ({ label, helper, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.25,
      px: 1.25,
      borderRadius: RADIUS_ITEM,
      transition: 'background-color 0.18s ease',
      '&:hover': { bgcolor: alpha(NAVY, 0.03) },
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        component="label"
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: NAVY,
          display: 'block',
        }}
      >
        {label}
      </Typography>
      {helper && (
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.75rem',
            color: alpha(NAVY, 0.55),
            mt: 0.25,
            lineHeight: 1.45,
          }}
        >
          {helper}
        </Typography>
      )}
    </Box>
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: YELLOW, opacity: 1 },
          }}
        />
      }
      label=""
      sx={{ m: 0 }}
    />
  </Box>
);

const EditClientForm: FC<EditClientFormProps> = ({
  client,
  clientDraft,
  setClientDraft,
  onSave,
  onCancel,
  currentUser = null,
  clientDealership = undefined,
  isSaving = false,
  saveError = null,
}) => {
  const isCarDealershipAssistant = clientDealership?.type === 'car_dealership';
  const isFinancialInstitution = clientDealership?.type === 'financial_institution';
  const isInsuranceBroker = clientDealership?.type === 'insurance_broker';
  const [emailError, setEmailError] = useState<string | null>(null);

  const draft = clientDraft;
  const update = (patch: Partial<EditableAdminClientAttributes>) => setClientDraft({ ...draft, ...patch });

  const emailIsValid = useMemo(() => !!draft.email && validateEmail(draft.email), [draft.email]);
  const useDni = !!client.dni;

  const addressDraft: Partial<Address> = useMemo(() => {
    const raw = draft.address;
    if (!raw || typeof raw === 'string') return {};
    return raw;
  }, [draft.address]);

  const updateAddress = (patch: Partial<Address>) => {
    update({ address: { ...addressDraft, ...patch } });
  };

  const missingInsurexFields = useMemo(() => {
    const missing: string[] = [];
    if (!draft.rut?.trim()) missing.push('RUT');
    if (!draft.gender) missing.push('Sexo');
    if (!draft.dateOfBirth) missing.push('Fecha de Nacimiento');
    if (!normalizeNationalityToIso2(draft.nationality)) missing.push('Nacionalidad');
    if (!draft.email?.trim()) missing.push('Email');
    const addressOk = !!addressDraft.street?.trim() && !!addressDraft.number?.trim() && !!addressDraft.commune?.trim();
    if (!addressOk) missing.push('Dirección');
    return missing;
  }, [
    draft.rut,
    draft.gender,
    draft.dateOfBirth,
    draft.nationality,
    draft.email,
    addressDraft.street,
    addressDraft.number,
    addressDraft.commune,
  ]);

  const parsedDateOfBirth = useMemo(() => {
    if (!draft.dateOfBirth) return null;
    const m = moment(draft.dateOfBirth, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
    return m.isValid() ? m : null;
  }, [draft.dateOfBirth]);

  const datePickerLabelSx = {
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: alpha(NAVY, 0.7),
    mb: 0.875,
    letterSpacing: '0.005em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
  };

  const datePickerFieldSx = {
    fontFamily: 'Jost, sans-serif',
    '& .MuiInputBase-root': { fontFamily: 'Jost, sans-serif' },
    '& .MuiOutlinedInput-root': {
      borderRadius: RADIUS_ITEM,
      bgcolor: SURFACE_CONTAINER_LOWEST,
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
      transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
      '& fieldset': { border: 'none' },
      '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
      '&.Mui-focused': { boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}` },
    },
    '& .MuiOutlinedInput-input': {
      color: NAVY,
      py: 1.4,
      px: 1.75,
      fontSize: '0.9375rem',
    },
    '& .MuiInputLabel-root': { display: 'none' },
  };

  const datePickerPopperSx = {
    '& .MuiPaper-root': {
      backdropFilter: GLASS_BLUR,
      backgroundColor: GLASS_BACKGROUND,
      boxShadow: SHADOW,
      borderRadius: RADIUS_CARD,
      border: `1px solid ${alpha(NAVY, 0.06)}`,
    },
  };

  return (
    <Box sx={{ fontFamily: 'Jost, sans-serif', pb: 2 }}>
      {saveError && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          No pudimos guardar los cambios. {saveError}
        </InlineFeedback>
      )}

      <FormSection title="Datos del cliente" description="Lo que el cliente ha compartido en la conversación." dense>
        <FormGrid columns={1} gap={2}>
          <FormField
            label="Nombre"
            value={draft.firstName || ''}
            onChange={(e) => update({ firstName: e.target.value })}
            placeholder="Ej: María"
            success={!!draft.firstName && draft.hasValidName !== false}
          />
          <FormField
            label="Apellido"
            value={draft.lastName || ''}
            onChange={(e) => update({ lastName: e.target.value })}
            placeholder="Ej: Pérez"
            success={!!draft.lastName && draft.hasValidName !== false}
          />
          <FormField
            label="Email"
            type="email"
            value={draft.email || ''}
            onChange={(e) => {
              update({ email: e.target.value });
              if (emailError) setEmailError(null);
            }}
            onBlur={() => {
              if (draft.email && !validateEmail(draft.email)) {
                setEmailError('Formato esperado: nombre@empresa.com');
              }
            }}
            placeholder="cliente@ejemplo.com"
            error={emailError}
            success={emailIsValid && !emailError}
            required={isInsuranceBroker}
            optional={!isInsuranceBroker}
          />
          {useDni ? (
            <FormField
              label="DNI"
              value={draft.dni || ''}
              onChange={(e) => update({ dni: e.target.value })}
              placeholder="Documento de identidad"
              required={isInsuranceBroker}
              optional={!isInsuranceBroker}
            />
          ) : (
            <RutField
              label="RUT"
              value={draft.rut || ''}
              onChange={(rut) => update({ rut })}
              hint="Lo cruzamos con el registro civil al guardar."
              required={isInsuranceBroker}
              optional={!isInsuranceBroker}
            />
          )}
          <FormField
            select
            required={isInsuranceBroker}
            optional={!isInsuranceBroker}
            label="Sexo"
            value={draft.gender ?? ''}
            onChange={(e) => update({ gender: e.target.value || undefined })}
          >
            <MenuItem value="M">Masculino</MenuItem>
            <MenuItem value="F">Femenino</MenuItem>
          </FormField>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography component="label" htmlFor="field-fecha-de-nacimiento" sx={datePickerLabelSx}>
              Fecha de Nacimiento
              {isInsuranceBroker ? (
                <Typography
                  component="span"
                  sx={{ color: '#E7233B', fontFamily: 'Jost, sans-serif', fontWeight: 600, ml: 0.25 }}
                  aria-hidden
                >
                  *
                </Typography>
              ) : (
                <Typography
                  component="span"
                  sx={{
                    color: alpha(NAVY, 0.45),
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.75rem',
                    ml: 0.5,
                  }}
                >
                  (opcional)
                </Typography>
              )}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD/MM/YYYY"
                value={parsedDateOfBirth}
                onChange={(value) =>
                  update({ dateOfBirth: value && value.isValid() ? value.format('YYYY-MM-DD') : undefined })
                }
                disableFuture
                slotProps={{
                  textField: {
                    id: 'field-fecha-de-nacimiento',
                    size: 'small',
                    fullWidth: true,
                    sx: datePickerFieldSx,
                  },
                  popper: { sx: datePickerPopperSx },
                }}
              />
            </LocalizationProvider>
          </Box>
          {(() => {
            const nationalityIso = normalizeNationalityToIso2(draft.nationality);
            // Legacy free-text values (e.g. "EXTRANJERA", "NACIONALIZADA") that
            // can't be mapped to an ISO-2 code: render the raw value as a
            // passthrough MenuItem so the Select shows the current value
            // instead of an empty box, and the admin can re-select to migrate.
            const rawNonIso = !nationalityIso && draft.nationality ? draft.nationality : null;
            const selectValue = nationalityIso ?? rawNonIso ?? '';
            return (
              <FormField
                select
                required={isInsuranceBroker}
                optional={!isInsuranceBroker}
                label="Nacionalidad"
                value={selectValue}
                onChange={(e) => update({ nationality: e.target.value || undefined })}
              >
                {rawNonIso && (
                  <MenuItem key={`__legacy__${rawNonIso}`} value={rawNonIso}>
                    {rawNonIso}
                  </MenuItem>
                )}
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </FormField>
            );
          })()}
        </FormGrid>
      </FormSection>

      <FormSection title="Dirección" description="Datos de la residencia del cliente." dense>
        <FormGrid columns={1} gap={2}>
          <FormField
            label="Calle"
            value={addressDraft.street ?? ''}
            onChange={(e) => updateAddress({ street: e.target.value || null })}
            placeholder="Ej: Av. Apoquindo"
            required={isInsuranceBroker}
            optional={!isInsuranceBroker}
          />
          <FormField
            label="Número"
            value={addressDraft.number ?? ''}
            onChange={(e) => updateAddress({ number: e.target.value || null })}
            placeholder="Ej: 1234"
            required={isInsuranceBroker}
            optional={!isInsuranceBroker}
          />
          <FormField
            label="Departamento"
            value={addressDraft.homeNumber ?? ''}
            onChange={(e) => updateAddress({ homeNumber: e.target.value || null })}
            placeholder="Ej: 502"
            optional
          />
          <FormField
            select
            label="Comuna"
            value={addressDraft.commune ?? ''}
            onChange={(e) => updateAddress({ commune: e.target.value || null })}
            required={isInsuranceBroker}
            optional={!isInsuranceBroker}
          >
            <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
              <em>Sin definir</em>
            </MenuItem>
            {addressDraft.commune && !CHILE_COMMUNES.includes(addressDraft.commune) && (
              <MenuItem
                key={`__current__${addressDraft.commune}`}
                value={addressDraft.commune}
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                {addressDraft.commune}
              </MenuItem>
            )}
            {CHILE_COMMUNES.map((commune) => (
              <MenuItem key={commune} value={commune} sx={{ fontFamily: 'Jost, sans-serif' }}>
                {commune}
              </MenuItem>
            ))}
          </FormField>
        </FormGrid>
      </FormSection>

      {isInsuranceBroker && missingInsurexFields.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <InlineFeedback severity="warning">
            Para subir este cliente al CRM completa: {missingInsurexFields.join(', ')}.
          </InlineFeedback>
        </Box>
      )}

      {isCarDealershipAssistant && currentUser?.isAdmin && (
        <FormSection title="Auto de interés" description="Detalles del vehículo que está cotizando." dense>
          <FormGrid columns={1} gap={2}>
            <FormField
              label="Pie disponible"
              type="number"
              value={draft.savingsAmount?.toString() ?? ''}
              onChange={(e) => update({ savingsAmount: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="0"
              hint="Monto en CLP que el cliente puede entregar al inicio."
              optional
            />
            <FormField
              label="Presupuesto total"
              type="number"
              value={draft.budgetAmount?.toString() ?? ''}
              onChange={(e) => update({ budgetAmount: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="0"
              hint="Tope que el cliente está dispuesto a pagar (CLP)."
              optional
            />
            <FormField
              label="Marca de interés"
              value={draft.brandOfInterest || ''}
              onChange={(e) => update({ brandOfInterest: e.target.value })}
              placeholder="Ej: Toyota"
              optional
            />
            <FormField
              label="Modelos de interés"
              value={(draft.modelsOfInterest || []).join(', ')}
              onChange={(e) =>
                update({
                  modelsOfInterest: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="Ej: RAV4, Corolla Cross"
              hint="Separa varios modelos con coma."
              optional
            />
          </FormGrid>

          <Box sx={{ mt: 2 }}>
            <AdminToggleRow
              label="¿Tiene un vehículo en parte de pago?"
              helper="Activa si el cliente entregará un auto usado como parte del pago."
              checked={!!draft.hasVehicleAsPartPayment}
              onChange={(v) => update({ hasVehicleAsPartPayment: v })}
            />
            <AdminToggleRow
              label="¿Requiere financiamiento?"
              helper="El bot ofrecerá opciones de crédito y consultará antecedentes."
              checked={!!draft.requiresFinancing}
              onChange={(v) => update({ requiresFinancing: v })}
            />
          </Box>
        </FormSection>
      )}

      {currentUser?.isAdmin && (
        <FormSection
          eyebrow="Solo administradores"
          title="Ajustes del bot para este cliente"
          description="Estos cambios afectan únicamente a esta conversación."
          dense
        >
          <Box>
            <AdminToggleRow
              label="Forzar respuestas siempre seguras"
              helper="El bot solo enviará mensajes ya validados. Útil para clientes sensibles."
              checked={draft.aiSafetyStatus === 'force-always-safe'}
              onChange={(v) => update({ aiSafetyStatus: v ? 'force-always-safe' : 'safe' })}
            />
            <AdminToggleRow
              label="Pausar respuestas automáticas"
              helper={
                canChangeAutomaticResponses(client.lastLeadStatus)
                  ? 'El bot dejará de responder. Útil mientras un humano toma la conversación.'
                  : 'No se puede cambiar en el estado actual del lead.'
              }
              checked={!!draft.disabledAutomaticResponses}
              disabled={!canChangeAutomaticResponses(client.lastLeadStatus)}
              onChange={(v) => update({ disabledAutomaticResponses: v })}
            />
            {!isFinancialInstitution && (
              <AdminToggleRow
                label="Desactivar follow-ups"
                helper="No enviaremos recordatorios automáticos a este cliente."
                checked={!!draft.disabledFollowUps}
                onChange={(v) => update({ disabledFollowUps: v })}
              />
            )}
            <AdminToggleRow
              label="Marcar como cliente de prueba"
              helper="No cuenta para métricas, reportes ni cobros."
              checked={!!draft.isTestClient}
              onChange={(v) => update({ isTestClient: v })}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormField
              label="Notas internas"
              value={draft.notes || ''}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Contexto que el equipo debería conocer…"
              multiline
              minRows={3}
              maxRows={6}
              hint="Solo visible para el equipo. No se muestra al cliente."
              optional
            />
          </Box>
        </FormSection>
      )}

      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 1,
          pt: 2,
          pb: 1,
          bgcolor: 'rgba(249, 250, 253, 0.9)',
          backdropFilter: 'blur(12px)',
          justifyContent: 'flex-end',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        <BackButton onClick={onCancel} disabled={isSaving}>
          Cancelar
        </BackButton>
        <PrimaryButton onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Guardando…' : 'Guardar cambios'}
        </PrimaryButton>
      </Stack>
    </Box>
  );
};

export default EditClientForm;
